import React, { useEffect, useState } from "react";
import { MessageCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const normPhone = (n) => {
  let d = (n || "").replace(/\D/g, "");
  if (d.length === 8) d = "502" + d; // Guatemala local number
  return d;
};

export function WhatsAppSendDialog({ open, onClose, docType, target, pdfPath }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ phone: "", filename: "", message: "" });

  useEffect(() => {
    if (!open || !target) return;
    setLoading(true);
    api.get(`/whatsapp-default/${docType}/${target.id}`)
      .then((r) => setForm({ phone: r.data.phone || "", filename: r.data.filename || "documento.pdf", message: r.data.message || "" }))
      .catch(() => setForm({ phone: "", filename: `${target.number || "documento"}.pdf`, message: "" }))
      .finally(() => setLoading(false));
  }, [open, target, docType]);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.get(pdfPath, { responseType: "blob" });
      let fn = (form.filename || "documento").trim();
      if (!fn.toLowerCase().endsWith(".pdf")) fn += ".pdf";
      const file = new File([res.data], fn, { type: "application/pdf" });
      // 1) Intentar compartir el ARCHIVO directamente (Web Share API). En móvil abre
      //    el menú de compartir con el PDF ya adjunto; eliges WhatsApp y el contacto.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: form.message || "", title: fn });
          toast.success("Compartido. Selecciona WhatsApp y el contacto para enviar el PDF.");
          onClose();
          return;
        } catch (err) {
          if (err && err.name === "AbortError") { setSending(false); return; } // el usuario canceló
          // cualquier otro error: caemos al respaldo (descarga + wa.me)
        }
      }
      // 2) Respaldo (equipos sin Web Share): descargar el PDF y abrir WhatsApp con el mensaje.
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fn; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const phone = normPhone(form.phone);
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(form.message || "")}`, "_blank");
      toast.info("Este equipo no permite adjuntar automáticamente. Descargamos el PDF y abrimos WhatsApp: solo adjunta el archivo descargado.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el PDF");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><MessageCircle className="h-5 w-5 text-green-600" /> Enviar por WhatsApp</DialogTitle>
          <DialogDescription>En el celular abre el menú de compartir con el PDF ya adjunto: elige WhatsApp y el contacto. En computadora, si el navegador no lo permite, se descarga el PDF y se abre WhatsApp para adjuntarlo.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Número de WhatsApp <span className="font-normal normal-case text-muted-foreground">(opcional)</span></Label>
              <Input className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Ej. 5555-1234" data-testid="wa-phone" />
              <p className="text-[11px] text-muted-foreground">Solo se usa en computadora (respaldo). En el celular el contacto se elige al compartir. Si es de Guatemala (8 dígitos) se agrega el 502 automáticamente.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Nombre del archivo</Label>
              <Input className="rounded-sm" value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })} data-testid="wa-filename" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Mensaje</Label>
              <Textarea className="rounded-sm" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="wa-message" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-sm font-bold gap-2 bg-green-600 hover:bg-green-700 text-white" onClick={send} disabled={sending || loading} data-testid="wa-send-btn">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Enviar por WhatsApp</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
